import {
  FC,
  ReactNode,
  StrictMode,
  useCallback,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { INSIGHT_PANEL_SCROLLING_DIV_ID } from '@/constants/constants';
import useBreakpoints, { BreakpointsEnum } from '@/hooks/useBreakpoint';
import useOverflow from '@/hooks/useOverflow';

import Spinner from '../common/loading/spinner';
import Divider from '../divider';

type Props = {
  children: ReactNode;
  /** CSS for the wrapper around the child and the Footer */
  parentClassName?: string;
  /** CSS for the wrapper around just the children */
  childrenClassName?: string;
  /** CSS for the wrapper around the entire header, excluding the children of the header */
  headerClassName?: string;
  /** CSS for the wrapper around the child */
  className?: string;
  isLoading?: boolean;
  Header?: ReactNode;
  Footer?: ReactNode;
  isExpandable?: boolean;
  /** The width at which an expandable insights wrapper starts */
  defaultExpandableWidth?: number;
};

const InsightsWrapper: FC<Props> = ({
  children,
  className = '',
  parentClassName = '',
  headerClassName = '',
  defaultExpandableWidth = 380,
  childrenClassName = '',
  isExpandable = false,
  isLoading = false,
  Header = null,
  Footer = null,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const { breakpoint } = useBreakpoints();
  const isMobile = breakpoint.down(BreakpointsEnum.SM);
  const [width, setWidth] = useState<number>(defaultExpandableWidth);
  const [isMouseDown, setIsMouseDown] = useState<boolean>(false);

  const { isTopOverflowing } = useOverflow(bodyRef, undefined);

  const handleMouseDown = useCallback((e) => {
    e.target.setPointerCapture(e.pointerId);
    setIsMouseDown(true);
    e.preventDefault();
  }, []);

  const handleMouseUp = useCallback((e) => {
    e.target.releasePointerCapture(e.pointerId);
    setIsMouseDown(false);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (isMouseDown) {
        setWidth(e.clientX - 83); // 83  = sidenav width + scroll bar width + half of grippy
      }
    },
    [isMouseDown]
  );

  return (
    <StrictMode>
      <div
        className="flex w-full"
        onPointerMove={handleMouseMove}
        onPointerUp={handleMouseUp}
      >
        <div
          ref={bodyRef}
          className={twMerge(
            'relative flex h-platMap max-h-platMap flex-col overflow-auto bg-white',
            parentClassName
          )}
          id={INSIGHT_PANEL_SCROLLING_DIV_ID}
        >
          <div
            className={twMerge(
              'flex w-[320px] min-w-[320px] max-w-[75vw] flex-grow flex-col content-stretch items-stretch overflow-x-hidden',
              className,
              isLoading && 'items-center justify-center',
              isMobile && '!w-screen max-w-[100%]'
            )}
            style={isExpandable ? { width } : {}}
          >
            {/* Outer div full width and sticky to prevent side overflow with absolute position children to go outside of div */}
            <div
              className={twMerge(
                'sticky top-0 z-80 bg-white',
                isTopOverflowing && 'border-b-1 border-gray-20',
                headerClassName
              )}
            >
              <div className={twMerge('px-4')}>{Header}</div>
            </div>
            <div
              className={twMerge(
                'flex-grow flex-col px-4 scrollbar-hide sm:scrollbar-default',
                childrenClassName
              )}
            >
              {isLoading ? <Spinner className="w-10" /> : children}
            </div>
            {Footer && (
              <div className="sticky bottom-0 z-80">
                <Divider />
                <div className="bg-white p-4">{Footer}</div>
              </div>
            )}
          </div>
        </div>

        {/* Grip icon thing */}
        {isExpandable && (
          <div
            className="flex cursor-ew-resize items-center justify-center px-1"
            onPointerDown={handleMouseDown}
          >
            <div className="h-9 w-[5px] rounded-full bg-gray-70" />
          </div>
        )}
      </div>
    </StrictMode>
  );
};

export default InsightsWrapper;
